import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Home, Plus, ArrowLeft, BedDouble, CalendarDays, Ticket, Building2, Trash2, Upload, RefreshCw, Link2, AlertTriangle } from "lucide-react";
import { eur } from "../lib/format";
import { ReorderableGallery } from "../components/ReorderableGallery";
import * as api from "../api";

const TYPES = [
  ["appartamento", "Appartamento"], ["casa_vacanze", "Casa vacanze"], ["bb", "B&B"],
  ["affittacamere", "Affittacamere"], ["hotel", "Hotel"], ["residence", "Residence"],
  ["agriturismo", "Agriturismo"], ["villaggio", "Villaggio"], ["altro", "Altro"],
];
const AMENITIES = [
  ["wifi", "Wi-Fi"], ["aria_condizionata", "Aria condizionata"], ["piscina", "Piscina"],
  ["cucina", "Cucina"], ["colazione", "Colazione"], ["vista_mare", "Vista mare"],
  ["parcheggio", "Parcheggio"], ["animali_ammessi", "Animali ammessi"], ["accessibilita", "Accessibilità"],
  ["riscaldamento", "Riscaldamento"], ["lavatrice", "Lavatrice"], ["tv", "TV"],
  ["balcone", "Balcone"], ["giardino", "Giardino"], ["bbq", "BBQ"],
];
const BED_TYPES = [
  ["matrimoniale", "Matrimoniale"], ["singolo", "Singolo"], ["doppio", "Doppio"],
  ["divano_letto", "Divano letto"], ["king", "King"], ["letto_a_castello", "Letto a castello"],
];
const TYPE_LABEL = Object.fromEntries(TYPES);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-slate-500">{label}</span>
    {children}
  </label>
);
const inputCls = "mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-secondary";

function ImageUploader({ label, value, multiple, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const images = multiple ? (value || []) : (value ? [value] : []);
  const upload = async (files) => {
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) { const r = await api.uploadMedia(f); urls.push(r.url); }
      if (multiple) onChange([...(value || []), ...urls]); else onChange(urls[0]);
      toast.success("Immagine caricata");
    } catch (e) { toast.error("Upload fallito"); } finally { setBusy(false); }
  };
  return (
    <div>
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <div className="mt-1 flex flex-wrap gap-2 items-center">
        {images.map((u, i) => (
          <div key={i} className="relative">
            <img src={u} alt="" className="h-16 w-20 object-cover rounded-lg border border-slate-200" />
            <button type="button" onClick={() => multiple ? onChange(value.filter((x) => x !== u)) : onChange("")}
              className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full h-5 w-5 text-xs">×</button>
          </div>
        ))}
        <label data-testid={testid} className="h-16 w-20 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center cursor-pointer text-slate-400 hover:border-secondary">
          {busy ? "..." : <Upload className="h-4 w-4" />}
          <input type="file" accept="image/*" multiple={multiple} className="hidden"
            onChange={(e) => upload(Array.from(e.target.files || []))} />
        </label>
      </div>
    </div>
  );
}

const EMPTY_ACC = {
  name: "", type: "appartamento", destination: "gargano", short_description: "", description: "",
  address: "", city: "", province: "", zip: "", region: "", phone: "", whatsapp: "", email: "", website: "",
  cin: "", cir: "", max_guests: 2, amenities: [], other_amenities: "", rules: "",
  pets_allowed: false, accessibility: false, parking: false, wifi: false, ac: false, pool: false,
  kitchen: false, breakfast: false, sea_view: false,
  checkin_time: "15:00", checkout_time: "10:00", checkin_time_selection_enabled: false, checkin_slots: [],
  cover: "", images: [], status: "DRAFT",
};

function StructureEditor({ initial, onDone, onCancel }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ ...EMPTY_ACC, ...initial });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleAmenity = (a) => set("amenities", f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a]);
  const mut = useMutation({
    mutationFn: (payload) => f.id ? api.opUpdateAccommodation(f.id, payload) : api.opCreateAccommodation(payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["opAcc"] }); toast.success("Struttura salvata"); onDone(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore nel salvataggio"),
  });
  const save = () => {
    if (!f.name) return toast.error("Il nome è obbligatorio");
    const { id, operator_id, slug, source, units, units_count, created_at, updated_at, ...payload } = f;
    mut.mutate(payload);
  };
  const BOOLS = [["wifi", "Wi-Fi"], ["ac", "Aria condizionata"], ["pool", "Piscina"], ["kitchen", "Cucina"],
    ["breakfast", "Colazione"], ["sea_view", "Vista mare"], ["parking", "Parcheggio"], ["pets_allowed", "Animali ammessi"], ["accessibility", "Accessibilità"]];

  return (
    <div className="space-y-5 max-w-3xl" data-testid="acc-structure-editor">
      <button onClick={onCancel} className="flex items-center gap-1 text-sm text-slate-500"><ArrowLeft className="h-4 w-4" /> Torna alle strutture</button>
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h3 className="font-heading font-semibold text-slate-900">{f.id ? "Modifica struttura" : "Nuova struttura"}</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nome struttura *"><input data-testid="acc-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></Field>
          <Field label="Tipologia"><select data-testid="acc-type" value={f.type} onChange={(e) => set("type", e.target.value)} className={inputCls}>{TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
          <Field label="Destinazione"><select value={f.destination} onChange={(e) => set("destination", e.target.value)} className={inputCls}><option value="gargano">Gargano</option><option value="dolomiti">Dolomiti</option></select></Field>
          <Field label="Max ospiti"><input type="number" min="1" value={f.max_guests} onChange={(e) => set("max_guests", parseInt(e.target.value) || 1)} className={inputCls} /></Field>
        </div>
        <Field label="Descrizione breve"><input value={f.short_description} onChange={(e) => set("short_description", e.target.value)} className={inputCls} /></Field>
        <Field label="Descrizione completa"><textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></Field>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h4 className="font-semibold text-slate-800 text-sm">Indirizzo & contatti</h4>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Indirizzo"><input value={f.address} onChange={(e) => set("address", e.target.value)} className={inputCls} /></Field>
          <Field label="Città"><input data-testid="acc-city" value={f.city} onChange={(e) => set("city", e.target.value)} className={inputCls} /></Field>
          <Field label="Provincia"><input value={f.province} onChange={(e) => set("province", e.target.value)} className={inputCls} /></Field>
          <Field label="CAP"><input value={f.zip} onChange={(e) => set("zip", e.target.value)} className={inputCls} /></Field>
          <Field label="Regione"><input value={f.region} onChange={(e) => set("region", e.target.value)} className={inputCls} /></Field>
          <Field label="Telefono"><input value={f.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></Field>
          <Field label="WhatsApp"><input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} /></Field>
          <Field label="Email"><input value={f.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></Field>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h4 className="font-semibold text-slate-800 text-sm">Dati turistici</h4>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="CIN"><input data-testid="acc-cin" value={f.cin} onChange={(e) => set("cin", e.target.value)} className={inputCls} /></Field>
          <Field label="CIR / CIS"><input value={f.cir} onChange={(e) => set("cir", e.target.value)} className={inputCls} /></Field>
        </div>
        <div>
          <span className="text-xs font-medium text-slate-500">Servizi</span>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {BOOLS.map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={!!f[k]} onChange={(e) => set(k, e.target.checked)} /> {l}
              </label>
            ))}
          </div>
        </div>
        <div>
          <span className="text-xs font-medium text-slate-500">Altri servizi</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {AMENITIES.map(([v, l]) => (
              <button key={v} type="button" onClick={() => toggleAmenity(v)}
                className={`rounded-full px-3 py-1 text-xs font-medium border ${f.amenities.includes(v) ? "bg-primary text-white border-primary" : "bg-white text-slate-500 border-slate-200"}`}>{l}</button>
            ))}
          </div>
        </div>
        <Field label="Regole struttura"><textarea rows={2} value={f.rules} onChange={(e) => set("rules", e.target.value)} className={inputCls} /></Field>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h4 className="font-semibold text-slate-800 text-sm">Check-in / Check-out</h4>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Ora check-in"><input type="time" value={f.checkin_time} onChange={(e) => set("checkin_time", e.target.value)} className={inputCls} /></Field>
          <Field label="Ora check-out"><input type="time" value={f.checkout_time} onChange={(e) => set("checkout_time", e.target.value)} className={inputCls} /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={f.checkin_time_selection_enabled} onChange={(e) => set("checkin_time_selection_enabled", e.target.checked)} />
          Consenti al cliente di scegliere una fascia di check-in
        </label>
        {f.checkin_time_selection_enabled && (
          <Field label="Fasce orarie (una per riga, es. 15:00-16:00)">
            <textarea rows={3} value={(f.checkin_slots || []).join("\n")} onChange={(e) => set("checkin_slots", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} className={inputCls} />
          </Field>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h4 className="font-semibold text-slate-800 text-sm">Foto</h4>
        <ImageUploader testid="acc-cover-upload" label="Immagine di copertina" value={f.cover} onChange={(v) => set("cover", v)} />
        <ReorderableGallery testid="acc-gallery-upload" value={f.images} onChange={(v) => set("images", v)} />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4 flex-wrap">
        <Field label="Stato">
          <select data-testid="acc-status" value={f.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
            <option value="DRAFT">Bozza (non pubblica)</option>
            <option value="ACTIVE">Attiva (pubblicata)</option>
            <option value="SUSPENDED">Sospesa</option>
          </select>
        </Field>
        <div className="flex-1" />
        <button data-testid="acc-save" onClick={save} disabled={mut.isPending} className="rounded-full bg-primary text-white font-semibold px-8 py-3 hover:bg-primary/90">{mut.isPending ? "Salvataggio..." : "Salva struttura"}</button>
      </div>
    </div>
  );
}

const EMPTY_UNIT = {
  name: "", description: "", quantity: 1, capacity_adults: 2, capacity_children: 0, capacity_total: 2,
  base_occupancy: 2, beds: 1, bed_types: [], bathrooms: 1, size_sqm: null, amenities: [], images: [],
  base_price: 0, weekend_price: null, extra_guest_price: 0, min_stay: 1, max_stay: null, active: true,
};

function UnitEditor({ accId, initial, onDone, onCancel }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ ...EMPTY_UNIT, ...initial });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleBed = (b) => set("bed_types", f.bed_types.includes(b) ? f.bed_types.filter((x) => x !== b) : [...f.bed_types, b]);
  const mut = useMutation({
    mutationFn: (payload) => f.id ? api.opUpdateUnit(f.id, payload) : api.opCreateUnit(accId, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["opAccDetail", accId] }); toast.success("Unità salvata"); onDone(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const save = () => {
    if (!f.name) return toast.error("Nome unità obbligatorio");
    const { id, accommodation_id, operator_id, created_at, updated_at, external_unit_id, ...p } = f;
    mut.mutate(p);
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4 max-w-2xl" data-testid="acc-unit-editor">
      <h4 className="font-semibold text-slate-800">{f.id ? "Modifica unità/camera" : "Nuova unità/camera"}</h4>
      <Field label="Nome (es. Camera Matrimoniale, Appartamento)"><input data-testid="unit-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></Field>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Quantità totale"><input data-testid="unit-quantity" type="number" min="0" value={f.quantity} onChange={(e) => set("quantity", parseInt(e.target.value) || 0)} className={inputCls} /></Field>
        <Field label="Capacità adulti"><input type="number" min="0" value={f.capacity_adults} onChange={(e) => set("capacity_adults", parseInt(e.target.value) || 0)} className={inputCls} /></Field>
        <Field label="Capacità bambini"><input type="number" min="0" value={f.capacity_children} onChange={(e) => set("capacity_children", parseInt(e.target.value) || 0)} className={inputCls} /></Field>
        <Field label="Capacità totale"><input type="number" min="1" value={f.capacity_total} onChange={(e) => set("capacity_total", parseInt(e.target.value) || 1)} className={inputCls} /></Field>
        <Field label="Occupazione inclusa"><input type="number" min="1" value={f.base_occupancy} onChange={(e) => set("base_occupancy", parseInt(e.target.value) || 1)} className={inputCls} /></Field>
        <Field label="Bagni"><input type="number" min="0" value={f.bathrooms} onChange={(e) => set("bathrooms", parseInt(e.target.value) || 0)} className={inputCls} /></Field>
        <Field label="Letti"><input type="number" min="0" value={f.beds} onChange={(e) => set("beds", parseInt(e.target.value) || 0)} className={inputCls} /></Field>
        <Field label="Superficie mq"><input type="number" min="0" value={f.size_sqm || ""} onChange={(e) => set("size_sqm", e.target.value ? parseFloat(e.target.value) : null)} className={inputCls} /></Field>
      </div>
      <div>
        <span className="text-xs font-medium text-slate-500">Tipologia letti</span>
        <div className="mt-2 flex flex-wrap gap-2">
          {BED_TYPES.map(([v, l]) => (
            <button key={v} type="button" onClick={() => toggleBed(v)}
              className={`rounded-full px-3 py-1 text-xs font-medium border ${f.bed_types.includes(v) ? "bg-primary text-white border-primary" : "bg-white text-slate-500 border-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Prezzo base / notte €"><input data-testid="unit-base-price" type="number" min="0" value={f.base_price} onChange={(e) => set("base_price", parseFloat(e.target.value) || 0)} className={inputCls} /></Field>
        <Field label="Prezzo weekend €"><input type="number" min="0" value={f.weekend_price || ""} onChange={(e) => set("weekend_price", e.target.value ? parseFloat(e.target.value) : null)} className={inputCls} /></Field>
        <Field label="Prezzo ospite extra €"><input type="number" min="0" value={f.extra_guest_price} onChange={(e) => set("extra_guest_price", parseFloat(e.target.value) || 0)} className={inputCls} /></Field>
        <Field label="Soggiorno minimo (notti)"><input type="number" min="1" value={f.min_stay} onChange={(e) => set("min_stay", parseInt(e.target.value) || 1)} className={inputCls} /></Field>
        <Field label="Soggiorno massimo (notti)"><input type="number" min="1" value={f.max_stay || ""} onChange={(e) => set("max_stay", e.target.value ? parseInt(e.target.value) : null)} className={inputCls} /></Field>
      </div>
      <ImageUploader testid="unit-images-upload" label="Immagini unità" value={f.images} multiple onChange={(v) => set("images", v)} />
      <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} /> Unità attiva</label>
      <div className="flex gap-3">
        <button data-testid="unit-save" onClick={save} disabled={mut.isPending} className="rounded-full bg-primary text-white font-semibold px-6 py-2.5">Salva</button>
        <button onClick={onCancel} className="rounded-full bg-slate-100 text-slate-600 font-semibold px-6 py-2.5">Annulla</button>
      </div>
    </div>
  );
}

function UnitSync({ unit }) {
  const qc = useQueryClient();
  const { data: feeds = [] } = useQuery({ queryKey: ["feeds", unit.id], queryFn: () => api.listIcalFeeds(unit.id) });
  const { data: exp } = useQuery({ queryKey: ["export", unit.id], queryFn: () => api.getIcalExport(unit.id) });
  const [showAdd, setShowAdd] = useState(false);
  const [feed, setFeed] = useState({ source_name: "Booking", feed_url: "" });
  const inval = () => { qc.invalidateQueries({ queryKey: ["feeds", unit.id] }); qc.invalidateQueries({ queryKey: ["unitCal", unit.id] }); };
  const add = useMutation({ mutationFn: (d) => api.addIcalFeed(unit.id, d), onSuccess: (r) => { inval(); setShowAdd(false); setFeed({ source_name: "Booking", feed_url: "" }); toast.success(`Feed collegato · ${r.sync?.events ?? 0} eventi`); }, onError: (e) => toast.error(e.response?.data?.detail || "Errore") });
  const sync = useMutation({ mutationFn: (fid) => api.syncIcalFeed(fid), onSuccess: (r) => { inval(); r.sync?.ok ? toast.success(`Sincronizzato · ${r.sync.events} eventi${r.sync.conflicts ? ` · ${r.sync.conflicts} conflitti` : ""}`) : toast.error(r.sync?.error || "Sync fallito"); }, onError: () => toast.error("Errore") });
  const del = useMutation({ mutationFn: (fid) => api.deleteIcalFeed(fid), onSuccess: () => { inval(); toast.success("Feed rimosso"); } });
  const disable = useMutation({ mutationFn: ({ fid, f }) => api.updateIcalFeed(fid, { ...f, active: !f.active }), onSuccess: () => { inval(); toast.success("Feed aggiornato"); } });
  const regen = useMutation({ mutationFn: () => api.regenerateIcalExport(unit.id), onSuccess: () => { qc.invalidateQueries({ queryKey: ["export", unit.id] }); toast.success("Link rigenerato · il vecchio non è più valido"); } });
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Link copiato"); };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3" data-testid={`unit-sync-${unit.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-slate-800 text-sm">Calendari collegati · {unit.name}</div>
        {!showAdd && <button data-testid="add-feed-btn" onClick={() => setShowAdd(true)} className="rounded-full bg-primary text-white text-xs font-semibold px-4 py-2 flex items-center gap-1"><Plus className="h-3 w-3" /> Collega calendario iCal</button>}
      </div>
      {showAdd && (
        <div className="rounded-lg bg-slate-50 p-3 space-y-3 mb-3" data-testid="feed-editor">
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Fonte</span>
              <select value={feed.source_name} onChange={(e) => setFeed({ ...feed, source_name: e.target.value })} className={inputCls}>
                {["Booking", "Airbnb", "Vrbo", "Expedia", "Altro"].map((x) => <option key={x} value={x}>{x}</option>)}
              </select></label>
            <label className="block sm:col-span-2"><span className="text-[10px] font-bold uppercase text-slate-400">URL feed iCal (.ics)</span>
              <input data-testid="feed-url" value={feed.feed_url} onChange={(e) => setFeed({ ...feed, feed_url: e.target.value })} placeholder="https://...ics" className={inputCls} /></label>
          </div>
          <p className="text-[11px] text-slate-400">Incolla solo un URL iCal (.ics) esportato dal portale. Non è una sincronizzazione in tempo reale garantita.</p>
          <div className="flex gap-2">
            <button data-testid="feed-save" onClick={() => { if (!feed.feed_url) return toast.error("Inserisci l'URL"); add.mutate(feed); }} disabled={add.isPending} className="rounded-full bg-primary text-white text-xs font-semibold px-5 py-2">{add.isPending ? "Collegamento..." : "Collega e sincronizza"}</button>
            <button onClick={() => setShowAdd(false)} className="rounded-full bg-slate-200 text-slate-600 text-xs font-semibold px-5 py-2">Annulla</button>
          </div>
        </div>
      )}
      {feeds.length === 0 ? <p className="text-xs text-slate-400">Nessun calendario esterno collegato.</p> : (
        <div className="space-y-2">
          {feeds.map((f) => (
            <div key={f.id} className="rounded-lg border border-slate-100 p-3 text-sm" data-testid={`feed-row-${f.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <span className="font-semibold text-slate-800">{f.source_name}</span>
                  {!f.active && <span className="ml-2 text-[10px] font-bold text-amber-600">DISATTIVO</span>}
                  <div className="text-[11px] text-slate-400">Ultimo sync: {f.last_sync_at ? new Date(f.last_sync_at).toLocaleString("it-IT") : "mai"} · Ultimo successo: {f.last_success_at ? new Date(f.last_success_at).toLocaleString("it-IT") : "—"}</div>
                  {f.last_error && <div className="text-[11px] text-red-500 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {f.last_error}</div>}
                </div>
                <div className="flex gap-3 text-xs font-semibold">
                  <button data-testid={`feed-sync-${f.id}`} onClick={() => sync.mutate(f.id)} className="text-primary flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Sincronizza ora</button>
                  <button onClick={() => disable.mutate({ fid: f.id, f })} className="text-slate-500">{f.active ? "Disattiva" : "Attiva"}</button>
                  <button onClick={() => { if (window.confirm("Eliminare il feed?")) del.mutate(f.id); }} className="text-red-500">Elimina</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {exp && (
        <div className="mt-4 rounded-lg bg-slate-50 p-3">
          <div className="text-[10px] font-bold uppercase text-slate-400 mb-1">Link di esportazione iCal (per Booking/Airbnb)</div>
          <div className="flex items-center gap-2 flex-wrap">
            <code data-testid="export-url" className="text-[11px] bg-white border border-slate-200 rounded px-2 py-1 break-all flex-1 min-w-0">{exp.export_url}</code>
            <button onClick={() => copy(exp.export_url)} className="rounded-full bg-slate-200 text-slate-600 text-xs font-semibold px-3 py-1.5 flex items-center gap-1"><Link2 className="h-3 w-3" /> Copia</button>
            <button data-testid="regen-export" onClick={() => { if (window.confirm("Rigenerare il link? Il vecchio smetterà di funzionare.")) regen.mutate(); }} className="rounded-full bg-slate-600 text-white text-xs font-semibold px-3 py-1.5">Rigenera</button>
          </div>
        </div>
      )}
    </div>
  );
}

const RATE_EMPTY = { name: "", start_date: "", end_date: "", nightly_price: 0, minimum_stay: 1, maximum_stay: null, active: true };

function SeasonalRates({ unit }) {
  const qc = useQueryClient();
  const { data: rates = [] } = useQuery({ queryKey: ["unitRates", unit.id], queryFn: () => api.opUnitRates(unit.id) });
  const [editing, setEditing] = useState(null);
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["unitRates", unit.id] }); qc.invalidateQueries({ queryKey: ["unitCal", unit.id] }); };
  const save = useMutation({
    mutationFn: (r) => r.id ? api.opUpdateRate(r.id, r) : api.opCreateRate(unit.id, r),
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Periodo salvato"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const del = useMutation({ mutationFn: api.opDeleteRate, onSuccess: () => { invalidate(); toast.success("Periodo eliminato"); }, onError: (e) => toast.error(e.response?.data?.detail || "Errore") });
  const dup = useMutation({ mutationFn: api.opDuplicateRate, onSuccess: (r) => { invalidate(); toast.success(`Duplicato: ${r.name}`); }, onError: (e) => toast.error(e.response?.data?.detail || "Errore") });

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3" data-testid={`seasonal-rates-${unit.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-slate-800 text-sm">Tariffe stagionali · {unit.name}</div>
        {editing === null && <button data-testid="new-rate-btn" onClick={() => setEditing({ ...RATE_EMPTY })} className="rounded-full bg-primary text-white text-xs font-semibold px-4 py-2 flex items-center gap-1"><Plus className="h-3 w-3" /> Nuovo periodo</button>}
      </div>
      {editing !== null ? (
        <div className="rounded-lg bg-slate-50 p-3 space-y-3" data-testid="rate-editor">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Nome periodo</span><input data-testid="rate-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Es. Alta stagione" className={inputCls} /></label>
            <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Prezzo/notte €</span><input data-testid="rate-price" type="number" min="0" value={editing.nightly_price} onChange={(e) => setEditing({ ...editing, nightly_price: parseFloat(e.target.value) || 0 })} className={inputCls} /></label>
            <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Data inizio</span><input data-testid="rate-start" type="date" value={editing.start_date} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} className={inputCls} /></label>
            <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Data fine</span><input data-testid="rate-end" type="date" value={editing.end_date} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} className={inputCls} /></label>
            <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Soggiorno minimo</span><input data-testid="rate-min" type="number" min="1" value={editing.minimum_stay} onChange={(e) => setEditing({ ...editing, minimum_stay: parseInt(e.target.value) || 1 })} className={inputCls} /></label>
            <label className="block"><span className="text-[10px] font-bold uppercase text-slate-400">Soggiorno massimo</span><input type="number" min="1" value={editing.maximum_stay || ""} onChange={(e) => setEditing({ ...editing, maximum_stay: e.target.value ? parseInt(e.target.value) : null })} className={inputCls} /></label>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Attivo</label>
          <div className="flex gap-2">
            <button data-testid="rate-apply" onClick={() => { if (!editing.name || !editing.start_date || !editing.end_date) return toast.error("Compila nome e date"); save.mutate(editing); }} disabled={save.isPending} className="rounded-full bg-primary text-white text-xs font-semibold px-5 py-2">Applica periodo</button>
            <button onClick={() => setEditing(null)} className="rounded-full bg-slate-200 text-slate-600 text-xs font-semibold px-5 py-2">Annulla</button>
          </div>
        </div>
      ) : rates.length === 0 ? (
        <p className="text-xs text-slate-400">Nessun periodo tariffario. Il prezzo base dell'unità viene applicato di default.</p>
      ) : (
        <div className="space-y-1">
          {rates.map((r) => (
            <div key={r.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm flex-wrap gap-2" data-testid={`rate-row-${r.id}`}>
              <div>
                <span className="font-semibold text-slate-800">{r.name}</span>
                {!r.active && <span className="ml-2 text-[10px] font-bold text-amber-600">DISATTIVO</span>}
                <span className="ml-2 text-xs text-slate-400">{r.start_date} → {r.end_date} · {eur(r.nightly_price)}/notte · min {r.minimum_stay}{r.maximum_stay ? ` · max ${r.maximum_stay}` : ""} notti</span>
              </div>
              <div className="flex gap-3 text-xs font-semibold">
                <button onClick={() => setEditing(r)} className="text-primary">Modifica</button>
                <button data-testid={`rate-dup-${r.id}`} onClick={() => dup.mutate(r.id)} className="text-slate-500">Duplica</button>
                <button onClick={() => { if (window.confirm("Eliminare il periodo?")) del.mutate(r.id); }} className="text-red-500">Elimina</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
function UnitCalendar({ unit }) {
  const qc = useQueryClient();
  const today = new Date();
  const [monthStart, setMonthStart] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [sel, setSel] = useState([]); // selected iso dates
  const fromIso = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1).toISOString().slice(0, 10);
  const { data } = useQuery({
    queryKey: ["unitCal", unit.id, fromIso],
    queryFn: () => api.opUnitCalendar(unit.id, { date_from: fromIso, days: 42 }),
  });
  const cal = Object.fromEntries((data?.calendar || []).map((c) => [c.date, c]));
  const setCal = useMutation({
    mutationFn: (payload) => api.opSetCalendar(unit.id, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["unitCal", unit.id] }); setSel([]); toast.success("Calendario aggiornato"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const daysInMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();
  const firstWd = (new Date(monthStart.getFullYear(), monthStart.getMonth(), 1).getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < firstWd; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(monthStart.getFullYear(), monthStart.getMonth(), d).toISOString().slice(0, 10));
  const toggle = (iso) => setSel((s) => s.includes(iso) ? s.filter((x) => x !== iso) : [...s, iso]);
  const apply = (action, extra = {}) => {
    if (sel.length === 0) return toast.error("Seleziona almeno un giorno");
    const sorted = [...sel].sort();
    setCal.mutate({ date_from: sorted[0], date_to: sorted[sorted.length - 1], action, ...extra });
  };
  const [price, setPrice] = useState("");
  const [minStay, setMinStay] = useState("");

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`unit-calendar-${unit.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-slate-800 text-sm">{unit.name} · <span className="text-slate-400">{unit.quantity} unità · da {eur(unit.base_price)}</span></div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() - 1, 1))} className="rounded-full bg-slate-100 h-8 w-8">‹</button>
          <span className="text-sm font-semibold w-28 text-center">{monthStart.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</span>
          <button onClick={() => setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1))} className="rounded-full bg-slate-100 h-8 w-8">›</button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {WD.map((w) => <div key={w} className="text-[10px] font-bold text-slate-400 py-1">{w}</div>)}
        {cells.map((iso, i) => {
          if (!iso) return <div key={`e${i}`} />;
          const c = cal[iso];
          const blocked = c?.blocked;
          const avail = c ? c.available : unit.quantity;
          const isSel = sel.includes(iso);
          return (
            <button key={iso} data-testid={`cal-day-${iso}`} onClick={() => toggle(iso)}
              className={`rounded-lg p-1 text-left border transition-colors min-h-[54px] ${isSel ? "border-secondary ring-2 ring-secondary/40" : "border-slate-100"} ${blocked ? "bg-red-50" : avail === 0 ? "bg-amber-50" : "bg-emerald-50/40"}`}>
              <div className="text-[11px] font-bold text-slate-600">{parseInt(iso.slice(8, 10))}</div>
              <div className={`text-[9px] font-semibold ${blocked ? "text-red-500" : avail === 0 ? "text-amber-600" : "text-emerald-600"}`}>{blocked ? "PIENA" : `${avail} disp`}</div>
              <div className="text-[9px] text-slate-400 flex items-center gap-0.5">{eur(c?.price ?? unit.base_price)}
                {c?.price_source === "seasonal" && <span className="text-secondary font-bold" title="Tariffa stagionale">S</span>}
                {c?.price_source === "override" && <span className="text-primary font-bold" title="Override manuale">O</span>}
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-4 rounded-lg bg-slate-50 p-3 space-y-3">
        <div className="text-xs font-semibold text-slate-500">{sel.length ? `${sel.length} giorni selezionati` : "Seleziona i giorni da modificare"}</div>
        <div className="flex flex-wrap gap-2 items-end">
          <div><span className="text-[10px] text-slate-400">Prezzo €</span><input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="w-24 rounded border border-slate-200 px-2 py-1 text-sm block" /></div>
          <div><span className="text-[10px] text-slate-400">Min notti</span><input type="number" value={minStay} onChange={(e) => setMinStay(e.target.value)} className="w-20 rounded border border-slate-200 px-2 py-1 text-sm block" /></div>
          <button data-testid="cal-apply-price" onClick={() => apply("update", { ...(price !== "" ? { price: parseFloat(price) } : {}), ...(minStay !== "" ? { min_stay: parseInt(minStay) } : {}) })}
            className="rounded-full bg-primary text-white text-xs font-semibold px-4 py-2">Applica prezzo/min</button>
          <button data-testid="cal-block" onClick={() => apply("block", { reason: "piena" })} className="rounded-full bg-red-500 text-white text-xs font-semibold px-4 py-2">Blocca / Piena</button>
          <button data-testid="cal-reopen" onClick={() => apply("reopen")} className="rounded-full bg-emerald-600 text-white text-xs font-semibold px-4 py-2">Riapri</button>
        </div>
      </div>
    </div>
  );
}

function AccommodationDetail({ accId, onBack }) {
  const qc = useQueryClient();
  const { data: acc, isLoading } = useQuery({ queryKey: ["opAccDetail", accId], queryFn: () => api.opGetAccommodation(accId) });
  const [tab, setTab] = useState("units");
  const [unitEdit, setUnitEdit] = useState(null);
  const { data: bookings = [] } = useQuery({ queryKey: ["opAccBookings"], queryFn: () => api.opAccBookings() });
  const { data: conflicts = [] } = useQuery({ queryKey: ["opConflicts"], queryFn: () => api.operatorConflicts() });
  const delUnit = useMutation({ mutationFn: api.opDeleteUnit, onSuccess: () => { qc.invalidateQueries({ queryKey: ["opAccDetail", accId] }); toast.success("Unità eliminata"); }, onError: (e) => toast.error(e.response?.data?.detail || "Errore") });
  if (isLoading) return <p className="text-slate-400">Caricamento...</p>;
  const accBookings = bookings.filter((b) => b.accommodation_id === accId);
  const accConflicts = conflicts.filter((c) => c.accommodation_id === accId);
  const SUBTABS = [["units", "Unità / Camere"], ["calendar", "Calendario"], ["sync", "Sincronizzazione"], ["bookings", "Prenotazioni"]];
  return (
    <div className="space-y-5" data-testid="acc-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500"><ArrowLeft className="h-4 w-4" /> Tutte le strutture</button>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-xl text-slate-900">{acc.name}</h3>
          <p className="text-sm text-slate-400">{TYPE_LABEL[acc.type]} · {acc.city} · <span className={`font-semibold ${acc.status === "ACTIVE" ? "text-emerald-600" : "text-amber-600"}`}>{acc.status}</span></p>
        </div>
      </div>
      <div className="flex gap-2 border-b border-slate-100">
        {SUBTABS.map(([k, l]) => (
          <button key={k} data-testid={`acc-subtab-${k}`} onClick={() => setTab(k)} className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === k ? "border-primary text-primary" : "border-transparent text-slate-400"}`}>{l}</button>
        ))}
      </div>

      {tab === "units" && (unitEdit !== null ? (
        <UnitEditor accId={accId} initial={unitEdit} onDone={() => setUnitEdit(null)} onCancel={() => setUnitEdit(null)} />
      ) : (
        <div className="space-y-3">
          <button data-testid="add-unit-btn" onClick={() => setUnitEdit({})} className="rounded-full bg-primary text-white font-semibold px-6 py-2.5 flex items-center gap-2"><Plus className="h-4 w-4" /> Nuova unità/camera</button>
          {(acc.units || []).length === 0 ? <p className="text-sm text-slate-400">Nessuna unità. Aggiungi la prima camera/appartamento.</p> : (
            <div className="grid sm:grid-cols-2 gap-4">
              {acc.units.map((u) => (
                <div key={u.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`unit-card-${u.id}`}>
                  <div className="flex items-start gap-3">
                    {u.images?.[0] && <img src={u.images[0]} alt="" className="h-14 w-16 object-cover rounded-lg" />}
                    <div className="flex-1">
                      <div className="font-semibold text-slate-800 text-sm">{u.name}</div>
                      <div className="text-xs text-slate-400">Qtà {u.quantity} · fino a {u.capacity_total} ospiti · {eur(u.base_price)}/notte · min {u.min_stay} notti</div>
                      {!u.active && <span className="text-[10px] font-bold text-amber-600">NON ATTIVA</span>}
                    </div>
                  </div>
                  <div className="mt-3 flex gap-3">
                    <button onClick={() => setUnitEdit(u)} className="text-xs font-semibold text-primary">Modifica</button>
                    <button onClick={() => { if (window.confirm("Eliminare l'unità?")) delUnit.mutate(u.id); }} className="text-xs font-semibold text-red-500 flex items-center gap-1"><Trash2 className="h-3 w-3" /> Elimina</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {tab === "calendar" && (
        <div className="space-y-6">
          {(acc.units || []).filter((u) => u.active).length === 0 ? <p className="text-sm text-slate-400">Aggiungi almeno un'unità attiva per gestire il calendario.</p> :
            acc.units.filter((u) => u.active).map((u) => (
              <div key={u.id}>
                <SeasonalRates unit={u} />
                <UnitCalendar unit={u} />
                <p className="mt-1 text-[10px] text-slate-400 flex gap-3"><span><b className="text-secondary">S</b> = tariffa stagionale</span><span><b className="text-primary">O</b> = override manuale</span><span>senza sigla = prezzo base</span></p>
              </div>
            ))}
        </div>
      )}

      {tab === "sync" && (
        <div className="space-y-4" data-testid="acc-sync-tab">
          {accConflicts.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="conflicts-panel">
              <div className="font-semibold text-amber-700 text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Conflitti di calendario ({accConflicts.length})</div>
              <div className="mt-2 space-y-1">
                {accConflicts.map((c) => (
                  <div key={c.id} className="text-xs text-amber-800">Prenotazione Cristalda <b>{c.booking_number}</b> in conflitto con evento {c.source} {c.ext_start}→{c.ext_end}. La prenotazione Cristalda è protetta.</div>
                ))}
              </div>
            </div>
          )}
          {(acc.units || []).filter((u) => u.active).length === 0 ? <p className="text-sm text-slate-400">Aggiungi un'unità attiva per gestire le sincronizzazioni.</p> :
            acc.units.filter((u) => u.active).map((u) => <UnitSync key={u.id} unit={u} />)}
        </div>
      )}

      {tab === "bookings" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>{["Numero", "Unità", "Check-in", "Check-out", "Notti", "Ospiti", "Totale", "Stato"].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {accBookings.length === 0 ? <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400">Nessuna prenotazione.</td></tr> :
                accBookings.map((b) => (
                  <tr key={b.id} data-testid={`acc-booking-${b.booking_number}`}>
                    <td className="px-4 py-3 font-semibold text-primary">{b.booking_number}</td>
                    <td className="px-4 py-3">{b.unit_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{b.check_in}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{b.check_out}</td>
                    <td className="px-4 py-3">{b.nights}</td>
                    <td className="px-4 py-3">{b.total_guests}</td>
                    <td className="px-4 py-3">{eur(b.gross_amount)}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-semibold ${b.status === "CONFIRMED" ? "text-emerald-600" : "text-slate-400"}`}>{b.status}</span></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function OperatorAccommodations() {
  const [view, setView] = useState({ mode: "list" });
  const { data: list = [], isLoading } = useQuery({ queryKey: ["opAcc"], queryFn: api.opAccommodations });
  const { data: ov } = useQuery({ queryKey: ["opAccOv"], queryFn: api.opAccommodationsOverview });

  if (view.mode === "edit") return <StructureEditor initial={view.initial} onDone={() => setView({ mode: "list" })} onCancel={() => setView({ mode: "list" })} />;
  if (view.mode === "detail") return <AccommodationDetail accId={view.accId} onBack={() => setView({ mode: "list" })} />;

  return (
    <div className="space-y-6" data-testid="operator-accommodations">
      {ov && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-[11px] font-bold uppercase text-slate-400">Strutture attive</div><div className="text-2xl font-bold text-primary">{ov.active_structures}/{ov.structures}</div></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-[11px] font-bold uppercase text-slate-400">Check-in oggi</div><div className="text-2xl font-bold text-slate-900">{ov.today.checkin}</div></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-[11px] font-bold uppercase text-slate-400">Check-out oggi</div><div className="text-2xl font-bold text-slate-900">{ov.today.checkout}</div></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-[11px] font-bold uppercase text-slate-400">Ospiti presenti</div><div className="text-2xl font-bold text-slate-900">{ov.today.guests_present}</div></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-[11px] font-bold uppercase text-slate-400">Domani in/out</div><div className="text-2xl font-bold text-slate-900">{ov.tomorrow.checkin}/{ov.tomorrow.checkout}</div></div>
        </div>
      )}
      <button data-testid="new-structure-btn" onClick={() => setView({ mode: "edit", initial: {} })} className="rounded-full bg-primary text-white font-semibold px-6 py-2.5 flex items-center gap-2"><Plus className="h-4 w-4" /> Nuova struttura</button>
      {isLoading ? <p className="text-slate-400">Caricamento...</p> : list.length === 0 ? (
        <div className="text-center py-16 text-slate-400"><Building2 className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>Nessuna struttura. Crea la tua prima struttura ricettiva.</p></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((a) => (
            <button key={a.id} data-testid={`structure-card-${a.id}`} onClick={() => setView({ mode: "detail", accId: a.id })}
              className="text-left bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="h-32 bg-slate-100">{(a.cover || a.images?.[0]) && <img src={a.cover || a.images[0]} alt="" className="h-full w-full object-cover" />}</div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800 text-sm">{a.name}</span>
                  <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${a.status === "ACTIVE" ? "bg-emerald-50 text-emerald-600" : a.status === "SUSPENDED" ? "bg-red-50 text-red-500" : "bg-amber-50 text-amber-600"}`}>{a.status}</span>
                </div>
                <div className="text-xs text-slate-400 mt-1">{TYPE_LABEL[a.type]} · {a.city} · {a.units_count} unità</div>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">Gestisci <BedDouble className="h-3 w-3" /></span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
